import {Component, Input, NgZone, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {DevicesService, LangService, NavigateService, ShortcutAction} from 'ngx-satoris';
import {DocumentType, UserPermission, UserPlatformRole} from '../../shared/models/user';
import {UiPermission} from 'src/app/shared/models/permission';
import {hasUiPerm} from 'src/app/shared/models/default-filter-strategy';
import {documentTypeIsVisa, documentTypeIsPassport} from 'src/app/shared/models/document-type-helpers';
import {ApiService} from '../../shared/services/api.service';
import {CartService} from '../../shared/services/cart.service';
import {ScanDocumentService} from 'src/app/shared/services/scan-document.service';
import {enumStringToArrayObject} from 'src/app/shared/utils/enumToArrayObject';
import {ConsumeMode} from 'src/app/shared/models/request';
import {QueueService} from '../../shared/services/queue.service';
import {Subscription} from 'rxjs';
import {NetworkPingState} from '../../shared/models/network';
import {LogItem} from '../../shared/models/queue';
import {Store} from '../../shared/models/store';
import {StorageService} from '../../shared/services/storage.service';
declare const window: any;
const permissions = window.cordova?.plugins?.permissions;

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class NavComponent {

  @Input() data: {label: string, route?: string, perm?: UserPermission, guard?: boolean, tip?: string, action?: () => void}[] = [
    {
      label: 'dashboard',
      route: '/dashboard',
      guard: !this.api.userRole?.isExternalMonitor,
      tip: 'tooltip.dashboard'
    },
    {
      label: 'menu.createRequests.DYN.',
      guard: this.api.userRole?.isCustomer,
      action: () => {
        this.checkOfflineQueueId().then((done: boolean) => {
          if(done) {
            this.cart.startApplication();
          }
        });
      },
      tip: 'tooltip.createRequests'
    },
    {
      label: 'trips.title',
      route: '/trips',
      guard: this.api.userRole?.isCustomer,
      tip: 'tooltip.trips'
    },
    {
      label: 'menu.people.DYN.',
      route: '/people',
      guard: this.api.userRole?.isCustomer && this.api.env.hasMultiPersonView,
      tip: 'tooltip.people'
    },
    {
      label: 'faq',
      guard: this.api.userRole?.isCustomer && !!this.api.env.faqSource,
      action: () => this.openFAQ(),
      tip: 'tooltip.faq'
    },
    {
      label: 'menu.users',
      route: '/users',
      perm: this.api.perm.ALLOW_ADMIN,
      guard: !this.api.userRole?.isCustomer,
      tip: 'tooltip.users'
    },
    {
      label: 'menu.requests.DYN.',
      route: '/admin-request',
      perm: this.getApplicationPerm(),
      guard: !this.api.userRole?.isCustomer,
      tip: 'tooltip.searchManually'
    },
    {
      label: 'menu.places',
      route: '/places',
      perm: this.api.perm.ALLOW_ADMIN,
      guard: !this.api.userRole?.isCustomer,
      tip: 'tooltip.places'
    },
    {
      label: 'dashboard.my_account',
      route: '/my-account',
      guard: true,
      tip: 'tooltip.my_account'
    }
  ];

  menuConsumeMode: {name: string, value: string | null}[] = [];
  userRoles = UserPlatformRole;
  UserPermission = UserPermission;
  progressUploadValues: number[] = [];
  progressDownloadValues: number[] = [];

  pingSubscription: Subscription;
  pingOnline: NetworkPingState = NetworkPingState.PENDING;

  constructor(public nav: NavigateService,
              public router: Router,
              public api: ApiService,
              public scanDocument: ScanDocumentService,
              public cart: CartService,
              public queue: QueueService,
              private deviceService: DevicesService,
              private storage: StorageService,
              private zone: NgZone,
              private lang: LangService) {
    this.api.progressUploadMap$.subscribe(() => {
      this.progressUploadValues = this.api.getProgressValues(true, 1);
    });
    this.api.progressDownloadMap$.subscribe(() => {
      this.progressDownloadValues = this.api.getProgressValues(false, 1);
    });

    let previousPong = true;
    this.pingSubscription = this.api.pingChange$.subscribe(pong => {
      this.pingOnline = pong ? NetworkPingState.ONLINE : NetworkPingState.OFFLINE;

      if(pong !== previousPong) {
        this.storage.saveToStorage<LogItem>(Store.LOGS_STORE, {id: new Date().getTime(), timestamp: new Date(), message: 'PING-PONG: ' + pong}, 1000).catch(console.error);
      }
      previousPong = pong;
    });

    this.menuConsumeMode = enumStringToArrayObject(ConsumeMode).map((item) => ({
      ...item,
      name: this.lang.transform(item.name.toLowerCase() + '.consume')
    }));
  }

  ngOnDestroy() {
    this.pingSubscription.unsubscribe();
  }

  clickScan() {
    this.checkOfflineQueueId().then((done: boolean) => {
      if(done) {
        if(this.deviceService.isDevices('cordova')) {
          permissions.checkPermission(permissions.CAMERA, (status: any) =>{
            if(status.hasPermission) {
              this.zone.run(() => {
                this.nav.to('scan-qr');
              });
            } else {
              this.zone.run(() => {
                this.nav.to('page-permission');
              });
            }
          });
        } else {
          this.nav.to('scan-qr');
        }
      }
    });
  }

  scanPassport() {
    this.checkOfflineQueueId().then((done: boolean) => {
      if(done) {
        this.nav.to('dashboard');
        setTimeout(() => {
          this.scanDocument.scanPassport();
        }, 500);
      }
    });
  }

  navTo(route: string) {
    this.checkOfflineQueueId().then((done: boolean) => {
      if(done) {
        this.nav.to(route);
      }
    });
  }

  openFAQ() {
    if (this.api.env.faqSource) {
      window.open(this.api.env.faqSource, '_blank');
    }
  }

  checkOfflineQueueId(): Promise<boolean> {
    if(this.queue.onNavDeleteOfflineQueueId) {
      return this.queue.warnOfflineOrphan().then((done: boolean) => {
        if(done) {
          return this.queue.removeFromQueue({id: this.queue.onNavDeleteOfflineQueueId}).then(() => true);
        } else {
          return false;
        }
      });
    } else {
      return Promise.resolve(true);
    }
  }

  getApplicationPerm(): UserPermission {
    if(this.api?.env.allowCreate) {
      if(this.api.hasPerm(this.api.perm.ALLOW_CONTROL)) {
        return this.api.perm.ALLOW_CONTROL;
      }
    }
    return this.api.perm.ALLOW_CREATE;
  }

  protected readonly DocumentType = DocumentType;
  protected readonly ShortcutAction = ShortcutAction;
  protected readonly NetworkPingState = NetworkPingState;
  protected readonly UserPlatformRole = UserPlatformRole;
  protected readonly UiPermission = UiPermission;
  documentTypeIsVisa = documentTypeIsVisa;
  documentTypeIsPassport = documentTypeIsPassport;
  hasUiPerm = hasUiPerm;
}
